/**
 * Local pre-upload analysis of a recorded voice take (TELV1-480 follow-up).
 *
 * The audio recorder (audio-recorder-polyfill) emits one complete WAV per
 * take: 44-byte canonical header + mono 16-bit PCM. Because we hold the
 * exact bytes that would be uploaded, duration and true RMS can be
 * computed deterministically — unlike the visual volume meter, which
 * listens on a SEPARATE getUserMedia capture and can disagree with what
 * was actually recorded (the iOS failure class this gate exists for).
 *
 * Thresholds come from the 2026-08 production incident measurements:
 * genuinely silent takes measured RMS 0.0003–0.002, real speech ≥ 0.04.
 * 0.008 sits a 5× margin above the silent cluster and a 5× margin below
 * the speech cluster. The duration floor catches accidental taps (0.13s
 * takes in the incident) before they burn a transcription round-trip.
 *
 * The server-side classifier remains the authority — this gate only stops
 * uploads that cannot possibly contain an answer.
 */

export interface WavTakeAnalysis {
  durationSeconds: number;
  rms: number;
}

export type WavTakeVerdict = 'ok' | 'too_short' | 'silent';

export const MIN_TAKE_DURATION_SECONDS = 0.7;
export const MIN_TAKE_RMS = 0.008;

const RIFF_HEADER_BYTES = 12; // 'RIFF' + size + 'WAVE'
const CHUNK_HEADER_BYTES = 8; // id + size
const MIN_WAV_BYTES = RIFF_HEADER_BYTES + CHUNK_HEADER_BYTES;

/**
 * Parse a single recorder-emitted WAV. The chunk layout is walked (not
 * assumed): an extended `fmt ` chunk or extra chunks before `data` are
 * handled, so RMS is only ever computed over actual PCM payload bytes.
 * Returns null when the bytes are not a parseable 16-bit PCM WAV —
 * callers must treat null as "cannot judge, let the server decide",
 * never as a rejection.
 */
export function analyzeWavTake(bytes: ArrayBuffer): WavTakeAnalysis | null {
  if (bytes.byteLength <= MIN_WAV_BYTES + 24) {
    // header-only file: the encoder captured zero samples (suspended
    // AudioContext / instantly-released tap)
    return { durationSeconds: 0, rms: 0 };
  }
  const view = new DataView(bytes);
  const isRiff = view.getUint32(0, false) === 0x52494646; // 'RIFF'
  const isWave = view.getUint32(8, false) === 0x57415645; // 'WAVE'
  if (!isRiff || !isWave) return null;

  // Walk the chunks to locate `fmt ` (byte rate, sample format) and `data`
  // (payload offset + size) instead of assuming the canonical 44-byte
  // layout.
  let byteRate = 0;
  let bitsPerSample = 0;
  let dataOffset = -1;
  let dataBytes = 0;
  let cursor = RIFF_HEADER_BYTES;
  while (cursor + CHUNK_HEADER_BYTES <= bytes.byteLength) {
    const chunkId = view.getUint32(cursor, false);
    const declared = view.getUint32(cursor + 4, true);
    const chunkStart = cursor + CHUNK_HEADER_BYTES;
    if (chunkId === 0x666d7420 && declared >= 16) {
      // 'fmt '
      byteRate = view.getUint32(chunkStart + 8, true);
      bitsPerSample = view.getUint16(chunkStart + 14, true);
    } else if (chunkId === 0x64617461) {
      // 'data' — payload may be truncated relative to the declared size
      dataOffset = chunkStart;
      dataBytes = Math.min(declared, bytes.byteLength - chunkStart);
      break;
    }
    if (declared === 0) break; // malformed: avoid an infinite walk
    cursor = chunkStart + declared + (declared % 2); // chunks are word-aligned
  }

  if (dataOffset < 0) return null; // no data chunk found — server decides
  if (bitsPerSample !== 16) return null; // only 16-bit PCM is understood

  const sampleCount = Math.floor(dataBytes / 2);
  if (!byteRate || sampleCount === 0) return { durationSeconds: 0, rms: 0 };

  let sumSquares = 0;
  for (let i = 0; i < sampleCount; i += 1) {
    const sample = view.getInt16(dataOffset + i * 2, true) / 32768;
    sumSquares += sample * sample;
  }

  return {
    durationSeconds: dataBytes / byteRate,
    rms: Math.sqrt(sumSquares / sampleCount),
  };
}

export function classifyWavTake(analysis: WavTakeAnalysis | null): WavTakeVerdict {
  if (!analysis) return 'ok'; // unknown shape — server decides
  if (analysis.durationSeconds < MIN_TAKE_DURATION_SECONDS) return 'too_short';
  if (analysis.rms < MIN_TAKE_RMS) return 'silent';
  return 'ok';
}
